import React, { useState, useCallback, useRef } from 'react'

import type { User } from '@ztechapps/common.contract'
import { TextInput, InlineEdit } from '@ztechapps/webui.input'
import { ExtendingRow, TableCellDragHandle } from '@ztechapps/webui.data-tools'
import { BusyButtonLink } from '@ztechapps/webui.button'
import { CellWithContextMenu } from '@ztechapps/webui.menu'
import { HorizontalStack } from '@ztechapps/webui.util'

import { TaskTemplateStatusActionEditor, StatusActionsBadge } from './TaskTemplateStatusActionEditor'
import type { TemplateStatusAction } from './TaskTemplateStatusActionEditor'
import { TaskTemplateMembersEditor, MembersBadge } from './TaskTemplateMembersEditor'

type InlineEditNameCellProps = {
  name: string
  onChange: (value: string) => void
}

const InlineEditNameCell = ({ name, onChange }: InlineEditNameCellProps) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(name)
  const inputRef = useRef<HTMLInputElement>(null)

  const startEdit = useCallback(() => {
    setDraft(name)
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [name])

  const commitEdit = useCallback(() => {
    setEditing(false)
    if (draft !== name) onChange(draft)
  }, [draft, name, onChange])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') commitEdit()
      if (e.key === 'Escape') {
        setDraft(name)
        setEditing(false)
      }
    },
    [commitEdit, name]
  )

  if (editing) {
    return (
      <td className="p-0">
        <TextInput
          value={draft}
          onChange={(e) => setDraft(e?.target?.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitEdit}
          inputRef={inputRef}
        />
      </td>
    )
  }

  return (
    <td className="p-2">
      <InlineEdit.Display onClick={startEdit}>{name || <span className="text-muted">---</span>}</InlineEdit.Display>
    </td>
  )
}

export type ListRowProps = {
  handleOnDelete: (taskId: string) => void
  handleOnChange: (value: string, taskId: string) => void
  handleStatusActionsChange: (statusActions: TemplateStatusAction[], taskId: string) => void
  handleMembersChange: (members: User[], taskId: string) => void
  // Transient drag-sort key — also the stable identity used to target mutations.
  _id?: string
  name: string
  members?: User[]
  statusActions?: TemplateStatusAction[]
}

export const ListRow = ({
  handleOnDelete,
  handleOnChange,
  handleStatusActionsChange,
  handleMembersChange,
  _id = '',
  name,
  members,
  statusActions,
  ...props
}: ListRowProps) => {
  const extensionRow = (
    <HorizontalStack gap={3} className="align-items-start">
      <div className="flex-grow-1">
        <small className="text-muted d-block mb-1">Members:</small>
        <TaskTemplateMembersEditor
          members={members}
          onChange={(updatedMembers) => handleMembersChange(updatedMembers, _id)}
        />
      </div>
      <div className="flex-grow-1">
        <small className="text-muted d-block mb-1">Actions:</small>
        <TaskTemplateStatusActionEditor
          statusActions={statusActions}
          onChange={(updatedActions) => handleStatusActionsChange(updatedActions, _id)}
        />
      </div>
    </HorizontalStack>
  )

  return (
    <ExtendingRow {...props} _id={_id} extensionRow={extensionRow} colSpan={4}>
      <TableCellDragHandle title="Drag to reorder" />
      <InlineEditNameCell name={name} onChange={(value) => handleOnChange?.(value, _id)} />
      <CellWithContextMenu
        className="p-2"
        contextMenuContent={<BusyButtonLink icon="trash" bsStyle="danger" onClick={() => handleOnDelete?.(_id)} />}
      >
        <HorizontalStack className={'justify-content-end align-items-center p-0'} gap={1}>
          <MembersBadge members={members} />
          <StatusActionsBadge count={statusActions?.length} />
        </HorizontalStack>
      </CellWithContextMenu>
    </ExtendingRow>
  )
}
